import { useState, useEffect } from "react";
import "./DatePickerModal.css";

const MONTHS = [
    "Gennaio", "Febbraio", "Marzo", "Aprile",
    "Maggio", "Giugno", "Luglio", "Agosto",
    "Settembre", "Ottobre", "Novembre", "Dicembre"
];

const DAYS_SHORT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const DatePickerModal = ({ value, onChange, min, max, label, className }) => {
    const [isTablet, setIsTablet] = useState(() => window.matchMedia("(max-width: 1024px)").matches);
    const [showModal, setShowModal] = useState(false);

    const today = new Date();
    const currentDate = value ? new Date(value + "T00:00:00") : today;

    const [viewYear, setViewYear] = useState(currentDate.getFullYear());
    const [viewMonth, setViewMonth] = useState(currentDate.getMonth());

    useEffect(() => {
        const mq = window.matchMedia("(max-width: 1024px)");
        const handler = (e) => setIsTablet(e.matches);
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, []);

    useEffect(() => {
        if (value) {
            const d = new Date(value + "T00:00:00");
            setViewYear(d.getFullYear());
            setViewMonth(d.getMonth());
        }
    }, [value]);

    const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

    const getFirstDayOfMonth = (year, month) => {
        const day = new Date(year, month, 1).getDay();
        return day === 0 ? 6 : day - 1;
    };

    const isDateDisabled = (year, month, day) => {
        const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        if (min && dateStr < min) return true;
        if (max && dateStr > max) return true;
        return false;
    };

    const handleDaySelect = (day) => {
        const newValue = `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        if (isDateDisabled(viewYear, viewMonth, day)) return;
        onChange({ target: { value: newValue } });
        setShowModal(false);
    };

    const handlePrevMonth = () => {
        if (viewMonth === 0) {
            setViewMonth(11);
            setViewYear(y => y - 1);
        } else {
            setViewMonth(m => m - 1);
        }
    };

    const handleNextMonth = () => {
        if (viewMonth === 11) {
            setViewMonth(0);
            setViewYear(y => y + 1);
        } else {
            setViewMonth(m => m + 1);
        }
    };

    const handleInputClick = (e) => {
        if (isTablet) {
            e.preventDefault();
            setShowModal(true);
        }
    };

    const formatDisplay = () => {
        if (!value) return "";
        const [y, m, d] = value.split("-");
        return `${parseInt(d)}/${parseInt(m)}/${y}`;
    };

    const selectedDateStr = value || "";

    const daysInMonth = getDaysInMonth(viewYear, viewMonth);
    const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

    const calendarCells = [];
    for (let i = 0; i < firstDay; i++) {
        calendarCells.push(null);
    }
    for (let d = 1; d <= daysInMonth; d++) {
        calendarCells.push(d);
    }

    return (
        <>
            {isTablet ? (
                <div className="date-picker-display" onClick={handleInputClick}>
                    <span>{formatDisplay() || "Seleziona data"}</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                </div>
            ) : (
                <input
                    type="date"
                    className={className || undefined}
                    value={value || ""}
                    onChange={onChange}
                    min={min || undefined}
                    max={max || undefined}
                />
            )}

            {showModal && isTablet && (
                <div className="date-picker-overlay" onClick={() => setShowModal(false)}>
                    <div className="date-picker-modal" onClick={e => e.stopPropagation()}>
                        <h3>{label || "Seleziona data"}</h3>

                        <div className="date-picker-month-nav">
                            <button type="button" onClick={handlePrevMonth}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="15 18 9 12 15 6" />
                                </svg>
                            </button>
                            <span className="date-picker-month-label">
                                {MONTHS[viewMonth]} {viewYear}
                            </span>
                            <button type="button" onClick={handleNextMonth}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="9 6 15 12 9 18" />
                                </svg>
                            </button>
                        </div>

                        <div className="date-picker-weekdays">
                            {DAYS_SHORT.map(d => (
                                <span key={d}>{d}</span>
                            ))}
                        </div>

                        <div className="date-picker-grid">
                            {calendarCells.map((day, i) => {
                                if (day === null) {
                                    return <span key={`empty-${i}`} className="date-picker-empty" />;
                                }
                                const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                                const isSelected = dateStr === selectedDateStr;
                                const disabled = isDateDisabled(viewYear, viewMonth, day);
                                return (
                                    <button
                                        key={day}
                                        type="button"
                                        className={`date-picker-day${isSelected ? " active" : ""}${disabled ? " disabled" : ""}`}
                                        onClick={() => handleDaySelect(day)}
                                        disabled={disabled}
                                    >
                                        {day}
                                    </button>
                                );
                            })}
                        </div>

                        <button
                            type="button"
                            className="date-picker-cancel"
                            onClick={() => setShowModal(false)}
                        >
                            Annulla
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default DatePickerModal;
